import { useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { useAuthStore } from "@/stores/auth.store"
import { useIsPWAStandalone } from "@/components/ui/external-link"
import { UserMenu } from "./UserMenu"
import { NotificationsMenu } from "./NotificationsMenu"
import { UpdateBar } from "./UpdateBar"
import { cn } from "@/lib/utils"
import { Icon } from "@/components/ui/icon"

interface NavbarProps {
  showAvatar?: boolean
  hideLoginButton?: boolean
}

export function Navbar({ showAvatar = false, hideLoginButton = false }: NavbarProps) {
  const { user } = useAuthStore()
  const isPWA = useIsPWAStandalone()
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (isPWA) {
      document.documentElement.dataset.pwa = "true"
    } else {
      delete document.documentElement.dataset.pwa
    }
    return () => {
      void (delete document.documentElement.dataset.pwa)
    }
  }, [isPWA])

  return (
    <>
    <header
      ref={headerRef}
      className={cn(
        "flex items-center justify-between whitespace-nowrap border-b border-border bg-background pl-[max(1.25rem,env(safe-area-inset-left,0px))] pr-[max(1.25rem,env(safe-area-inset-right,0px))] sm:pl-6 sm:pr-6 lg:pl-10 lg:pr-10 sticky top-0 z-50 safe-area-inset-top navbar-header",
        isPWA ? "min-h-24 lg:min-h-14 pt-3 pb-3 lg:pt-2 lg:pb-3" : "min-h-16 pb-2 lg:min-h-14 lg:pb-2"
      )}
    >
      <div className="flex items-center gap-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 text-foreground hover:opacity-80 transition-opacity shrink-0 min-w-0">
          <img src="/img/favicon.svg" alt="LIZTA" className="h-8 w-auto" />
          <span className="font-bold text-xl tracking-tight">LIZTA</span>
        </Link>

        {/* Navigation Links - Only show when user is logged in */}
        {user && (
          <div className="hidden lg:flex items-center gap-6">
            <Link
              className="text-muted-foreground text-sm font-medium leading-normal hover:text-primary transition-colors"
              to="/"
            >
              Início
            </Link>
            <Link
              className="text-muted-foreground text-sm font-medium leading-normal hover:text-primary transition-colors"
              to="/my-games/today"
            >
              Meu Dia
            </Link>
            <Link
              className="text-muted-foreground text-sm font-medium leading-normal hover:text-primary transition-colors"
              to="/my-ingressos"
            >
              Meus Ingressos
            </Link>
            <Link
              className="text-muted-foreground text-sm font-medium leading-normal hover:text-primary transition-colors"
              to="/my-teams"
            >
              Meus Times
            </Link>
          </div>
        )}
      </div>

      <div className="flex flex-1 justify-end items-center gap-4 lg:gap-6">
        {/* Notifications and Avatar */}
        {showAvatar && user ? (
          <div className="flex items-center gap-4 lg:gap-6">
            <Link
              to="/support"
              aria-label="Ajuda e suporte"
              className="hidden lg:flex items-center justify-center overflow-hidden rounded-full size-10 bg-muted/30 dark:bg-secondary text-foreground dark:text-white hover:bg-muted dark:hover:bg-muted transition-colors"
            >
              <Icon name="support_agent" />
            </Link>
            <NotificationsMenu navbarRef={headerRef} />
            <div className="flex items-center self-stretch pl-4 border-l border-border">
              <UserMenu navbarRef={headerRef} userEmail={user.email} userName={user.name} userAvatar={user.avatar} />
            </div>
          </div>
        ) : !hideLoginButton && (
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              className="inline-flex h-10 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-95 shadow-sm shadow-primary/20"
            >
              Entrar
            </Link>
          </div>
        )}
      </div>
    </header>
    <UpdateBar />
    </>
  )
}
